import React, { useCallback, useEffect, useRef } from 'react';
import { InView } from 'react-intersection-observer';
import cx from 'classnames';
import { isEmpty } from 'lodash';
import snakeCase from 'lodash/snakeCase';

import 'swiper/css';
import 'swiper/css/free-mode';
import 'swiper/css/navigation';
import 'swiper/css/thumbs';

import { BackgroundLayout } from 'Components/BackgroundLayout';
import cypressIds from 'Components/cypress_ids.json';
import { PageSectionLayout } from 'Components/PageSectionLayout';
import { QuickLinks } from 'Components/QuickLinks';
import { VehicleRatings } from 'Components/VehicleRatings';
import { useBrazeProvider } from 'Context/BrazeContext/BrazeContext';
import { useVehicleData } from 'Context/VehicleContext/VehicleDataContext';
import { PaidListingInfoBox } from 'Features/PaidListings';
import { IMAGE_KIND } from 'Pages/vehicles/helpers/images.const';
import { ANALYTICS_CATEGORIES } from 'Services/analytics/analytics.const';
import { triggerSectionScrollEvent } from 'Services/analytics/events';
import { sendVehicleViewedBrazeEvent } from 'Services/braze/brazeEvents.helpers';
import { trackVehicleViewed } from 'Services/gtm';
import { logger } from 'Services/logger/logger';
import { useOptimizelyEarlyDocumentsExperimentDecision } from 'Services/optimizely/experimentations/useEarlyDocumentsExperimentation';
import { useSeenFilterExperimentation } from 'Services/optimizely/experimentations/useSeenFilterExperimentation';
import { EXPERIMENTS } from 'Services/optimizely/optimizely.consts';
import { useOptimizelyDecision } from 'Services/optimizely/useOptimizelyDecision';
import {
	useGetActiveSection,
	useInitSections,
	useResetSections,
	useSetActiveSection,
} from 'Stores/VehicleDetailsPositionStore/VehicleDetailsPositionStore';
import { applyCypressData } from 'Utilities/helpers';
import useFeatureToggle, { FEATURES } from 'Utilities/hooks/useFeatureToggle';
import { useIsUserDisabled } from 'Utilities/hooks/useIsUserDisabled';
import useUser from 'Utilities/hooks/useUser';
import { markVehicleAsSeen } from 'Utilities/seenVehicles/seenVehicles';

import { BiddingSection } from './sections/BiddingSection/BiddingSection';
import { StickyBiddingSection } from './sections/BiddingSection/StickyBiddingSection';
import { InsightsSection } from './sections/InsightsSection';
import { MainInfoSection } from './sections/MainInfoSection/MainInfoSection';
import { SimilarVehiclesModal } from './sections/SimilarVehiclesSection/SimilarVehiclesModal/SimilarVehiclesModal';
import { useSimilarVehiclesContext } from './sections/SimilarVehiclesSection/SimilarVehiclesProvider/SimilarVehiclesProvider';
import { SimilarVehiclesSection } from './sections/SimilarVehiclesSection/SimilarVehiclesSection';
import { VehiclePhotosSection } from './sections/VehiclePhotosSection';
import { SECTIONS } from './VehicleDetailsRedesign.const';
import { hasGalleryImagesByKind } from './VehicleDetailsRedesign.helpers';
import { useLayoutCalc, useSections } from './VehicleDetailsRedesign.hooks';
import { VehicleDetailsRedesignGallery } from './VehicleDetailsRedesignGallery';

import styles from './VehicleDetailsRedesign.module.scss';

export const VehicleDetailsRedesign: React.FC = () => {
	const isUserDisabled = useIsUserDisabled();
	const user = useUser();
	const vehicle = useVehicleData();
	const { earlyDocuments } = vehicle.details;
	const isEarlyDocumentsVisible = useOptimizelyEarlyDocumentsExperimentDecision({
		hasEarlyDocuments: !!earlyDocuments?.hasEarlyDocuments,
		id: vehicle.id,
		isPaidListing: !!vehicle.details.isPaidListing,
	});
	const additionalImagesDecision = useOptimizelyDecision({ experimentId: EXPERIMENTS.ADDITIONAL_PHOTOS });
	const isSeenFilterEnabled = useSeenFilterExperimentation();

	const isExLease = useFeatureToggle(FEATURES.exLeaseBranding);
	const hasRelistDetails = !isEmpty(vehicle.relistDetails.name) && !isEmpty(vehicle.relistDetails.info);
	const isMobilizeVehicle = vehicle.sourcePartner?.toLowerCase() === 'mobilize';
	const hasAdditionalImages = hasGalleryImagesByKind(vehicle.gallery.images, [IMAGE_KIND.additionalImage]);
	const shouldShowAdditionalImages =
		hasAdditionalImages && additionalImagesDecision.isEnabled && additionalImagesDecision.decision === 'on';
	const isExLeaseVehicle = isExLease && vehicle.seller?.isTrade && !isMobilizeVehicle;
	const { biddingRef, contentRef, isDesktop, similarRef, stickyRef } = useLayoutCalc();
	const { isBrazeSDKInitialised } = useBrazeProvider();
	const activeSection = useGetActiveSection();
	const setActiveSection = useSetActiveSection();
	const resetSections = useResetSections();
	const { quickLinks, sections } = useSections({
		hasAdditionalImages: shouldShowAdditionalImages,
		hasEarlyDocuments: !!earlyDocuments?.hasEarlyDocuments && isEarlyDocumentsVisible,
		hasRelistDetails,
		isExLeaseVehicle: !!isExLeaseVehicle,
		isMobilizeVehicle: !!isMobilizeVehicle,
		isTrade: vehicle.seller.isTrade,
	});
	const { isSimilarVehiclesModalOpen, isSimilarVehiclesSectionVisible } = useSimilarVehiclesContext();
	const initSections = useInitSections();

	const shouldShowPaidListingInfo = Boolean(vehicle.details.isPaidListing);

	useEffect(() => {
		initSections([...SECTIONS]);
	}, [initSections]);

	useEffect(() => {
		trackVehicleViewed({ enquiryId: vehicle.id, vrm: vehicle.mainInfo.prettyVrm });
	}, [vehicle.id, vehicle.mainInfo.prettyVrm]);

	const markedSeenRef = useRef(false);
	const markSeenRef = useCallback(
		(node: HTMLDivElement | null) => {
			if (node && user?.id && !markedSeenRef.current && isSeenFilterEnabled) {
				markedSeenRef.current = true;
				markVehicleAsSeen(user.id, vehicle.id);
			}
		},
		[user?.id, vehicle.id, isSeenFilterEnabled],
	);

	useEffect(() => {
		if (isBrazeSDKInitialised) {
			sendVehicleViewedBrazeEvent({ enquiryId: vehicle.id, slug: vehicle.state.slug }).catch(({ message }) =>
				logger.warn({ message, scope: 'sendVehicleViewedBrazeEvent' }),
			);
		}
	}, [vehicle.id, vehicle.state.slug, isBrazeSDKInitialised]);

	useEffect(() => {
		return () => {
			resetSections([...SECTIONS]);
		};
	}, [resetSections]);

	const onSectionActiveChange = ({ inView, name }: { inView: boolean; name: string }) => {
		if (inView) {
			setActiveSection(name);

			if (!activeSection?.id || activeSection?.id === name) return;
			triggerSectionScrollEvent({
				category: 'Vehicle details',
				from: snakeCase(activeSection?.id),
				to: snakeCase(name),
			});
		}
	};

	return (
		<div ref={markSeenRef} className={styles.page}>
			<VehicleDetailsRedesignGallery>
				<div className={styles.container} {...applyCypressData(cypressIds.containers.vehicle)}>
					<div className={styles.summary}>
						<div className={styles.gallery} id="summary">
							<InView
								as="div"
								data-testid="summary"
								onChange={(inView) => onSectionActiveChange({ inView, name: 'summary' })}
							>
								<VehiclePhotosSection />
							</InView>
						</div>
						<div className={styles.sidebar}>
							<MainInfoSection />
							<VehicleRatings
								analyticsCategory={ANALYTICS_CATEGORIES.VEHICLE_DETAILS}
								legacyGrade={vehicle.ratings.grade}
								numericGrade={vehicle.ratings.numericGrade}
								previousOwners={vehicle.ratings.previousOwners}
								serviceHistory={vehicle.ratings.serviceHistory}
							/>
							<div ref={stickyRef} className={styles.stickyContainer}>
								<div ref={biddingRef} className={styles.bidding}>
									<BiddingSection />
									{shouldShowPaidListingInfo && (
										<div className={styles.hideOnMobileWrapper}>
											<PaidListingInfoBox />
										</div>
									)}
								</div>
								<div ref={similarRef} className={styles.extras}>
									{shouldShowPaidListingInfo && (
										<div className={cx(styles.hideOnDesktopWrapper, styles.paidListingMobile)}>
											<PaidListingInfoBox />
										</div>
									)}
									{!isUserDisabled && isSimilarVehiclesSectionVisible && (
										<div className={styles.similar}>
											<BackgroundLayout backgroundColour="light" border="none">
												<SimilarVehiclesSection />
											</BackgroundLayout>
										</div>
									)}
									{/* We need to make room for paid listing by replacing insights */}
									{!isUserDisabled && !shouldShowPaidListingInfo && (
										<div className={styles.insights} data-testid="insightsSection">
											<InsightsSection />
										</div>
									)}
								</div>
							</div>
						</div>
					</div>
					<div ref={contentRef} className={styles.content}>
						<div className={styles.navigation}>
							<div className={styles.navLinks}>
								<QuickLinks
									analytics={{
										category: ANALYTICS_CATEGORIES.VEHICLE_DETAILS,
									}}
									sections={quickLinks}
								/>
							</div>
						</div>
						<div className={styles.sections} {...applyCypressData()}>
							{sections.map(({ action, className, component, id: sectionId, label, title }) => (
								<InView
									key={sectionId}
									as="div"
									data-testid={sectionId}
									{...applyCypressData(sectionId)}
									onChange={(inView) => onSectionActiveChange({ inView, name: sectionId })}
									rootMargin="0px 0px -100% 0px"
								>
									<PageSectionLayout
										action={action?.()}
										backgroundLayout={{
											backgroundColour: 'light',
											border: 'none',
											className,
											id: sectionId,
										}}
										title={title ?? label}
									>
										{component?.()}
									</PageSectionLayout>
								</InView>
							))}
						</div>
					</div>
				</div>
				{isSimilarVehiclesModalOpen ? <SimilarVehiclesModal /> : null}
			</VehicleDetailsRedesignGallery>
			{/* Sticky footer for mobile bidding */}
			{!isDesktop ? <StickyBiddingSection /> : null}
		</div>
	);
};

export default VehicleDetailsRedesign;
